import React, { useState, useEffect } from 'react';
import { schoolAdminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const PayFeePage = () => {
  const [loading, setLoading] = useState(false);
  const [school, setSchool] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [payments, setPayments] = useState([]);
  const [manualRequests, setManualRequests] = useState([]);
  
  // USDT modal
  const [showUSDTModal, setShowUSDTModal] = useState(false);
  const [usdtDetails, setUsdtDetails] = useState(null);
  
  // Bank transfer modal
  const [showBankModal, setShowBankModal] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  
  // Common credit amount input
  const [creditAmount, setCreditAmount] = useState(50000);

  useEffect(() => {
    fetchFullData();
  }, []);

  const fetchFullData = async () => {
    setLoading(true);
    try {
      const res = await schoolAdminAPI.getSchoolInfo();
      const data = res.data;
      setSchool(data.school || null);
      setWallet(data.wallet || null);
      setPayments(data.payments || []);
      setManualRequests(data.manual_payment_requests || []);
    } catch (error) {
      toast.error('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  // ========== Paystack (NGN) – Application Fee ==========
  const handlePayFeeNGN = async () => {
    setLoading(true);
    try {
      const res = await schoolAdminAPI.initiatePayment({
        payment_type: 'application_fee',
        callback_url: window.location.origin + '/admin/payment-callback',
      });
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error('Payment initiation failed');
      setLoading(false);
    }
  };

  // ========== USDT – Application Fee ==========
  const handleUSDTFee = async () => {
    setLoading(true);
    try {
      const res = await schoolAdminAPI.initiateUSDT({
        payment_type: 'application_fee',
      });
      setUsdtDetails(res.data);
      setShowUSDTModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'USDT initiation failed');
    } finally {
      setLoading(false);
    }
  };

  // ========== Buy Credits – Paystack (NGN) ==========
  const handleBuyCreditsPaystack = async () => {
    if (creditAmount < 1) {
      toast.error('Enter at least 1 credit');
      return;
    }
    setLoading(true);
    try {
      const res = await schoolAdminAPI.initiatePayment({
        payment_type: 'credit_purchase',
        credits: creditAmount,
        callback_url: window.location.origin + '/admin/payment-callback',
      });
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Paystack initiation failed');
      setLoading(false);
    }
  };

  // ========== Buy Credits – Bank Transfer ==========
  const handleBuyCreditsBank = async () => {
    if (creditAmount < 1) {
      toast.error('Enter at least 1 credit');
      return;
    }
    // Convert credits to NGN (1 credit = 1 NGN) – adjust if needed
    const amountNgn = creditAmount; // since 1 credit = 1 NGN
    if (amountNgn < 10000) {
      toast.error('Minimum bank transfer is ₦10,000 (10,000 credits)');
      return;
    }
    setLoading(true);
    try {
      const res = await schoolAdminAPI.requestManualTopup({ amount: amountNgn });
      setBankDetails(res.data);
      setShowBankModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bank transfer initiation failed');
    } finally {
      setLoading(false);
    }
  };

  // ========== Buy Credits – USDT ==========
  const handleBuyCreditsUSDT = async () => {
    if (creditAmount < 1) {
      toast.error('Enter at least 1 credit');
      return;
    }
    const usdtEquiv = creditAmount * 0.001;
    if (usdtEquiv < 50) {
      toast.error(`Minimum USDT top-up is 50 USDT (50,000 credits). You entered ${creditAmount} credits (${usdtEquiv.toFixed(2)} USDT).`);
      return;
    }
    setLoading(true);
    try {
      const res = await schoolAdminAPI.initiateUSDT({
        payment_type: 'credit_purchase',
        credits: creditAmount,
      });
      setUsdtDetails(res.data);
      setShowUSDTModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'USDT initiation failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading && !school) return <div className="text-center py-10">Loading payment details...</div>;
  if (!school) return <div className="text-center py-10">No school data found.</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
      {/* School Info */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h1 className="text-2xl font-bold mb-4">School: {school.name}</h1>
        <p className="text-gray-600">Approval Status: <span className="font-medium">{school.approval_status}</span></p>
        <p className="text-gray-600">Application Fee Paid: 
          <span className={`ml-2 font-medium ${school.application_fee_paid ? 'text-green-600' : 'text-red-600'}`}>
            {school.application_fee_paid ? 'Yes' : 'No'}
          </span>
        </p>
      </div>

      {/* Wallet Balance */}
      {wallet && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-3">Wallet</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600">Credit Balance</p>
              <p className="text-2xl font-bold">{wallet.credit_balance.toLocaleString()}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600">Total Credits Purchased</p>
              <p className="text-2xl font-bold">{wallet.total_credits_purchased.toLocaleString()}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-yellow-600">Total Credits Used</p>
              <p className="text-2xl font-bold">{wallet.total_credits_used.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Application Fee (if not paid) */}
      {!school.application_fee_paid && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4">Pay Application Fee</h2>
          <p className="mb-4">Your school is approved. Please pay the one-time application fee to activate your account.</p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handlePayFeeNGN}
              disabled={loading}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Pay with Paystack (NGN)'}
            </button>
            <button
              onClick={handleUSDTFee}
              disabled={loading}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Pay with USDT (ERC20)
            </button>
          </div>
        </div>
      )}

      {/* ===== Buy Credits – Combined Section ===== */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-bold mb-4">Buy Credits</h2>
        <p className="text-sm text-gray-600 mb-4">
          Choose your preferred payment method. All payments are securely processed.
        </p>

        {/* Credit Amount Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Number of Credits</label>
          <input
            type="number"
            min="1"
            value={creditAmount}
            onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
            className="mt-1 block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Payment Methods Buttons */}
        <div className="flex flex-wrap gap-4">
          {/* Paystack (NGN) */}
          <button
            onClick={handleBuyCreditsPaystack}
            disabled={loading || creditAmount < 1}
            className="bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Processing...' : `Paystack (₦${creditAmount.toLocaleString()})`}
          </button>

          {/* Bank Transfer */}
          <button
            onClick={handleBuyCreditsBank}
            disabled={loading || creditAmount < 1}
            className="bg-purple-600 text-white px-5 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            Bank Transfer
          </button>

          {/* USDT */}
          <button
            onClick={handleBuyCreditsUSDT}
            disabled={loading || creditAmount < 1}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            USDT ({(creditAmount * 0.001).toFixed(2)} USDT)
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          Minimums: ₦10,000 for Bank Transfer, 50 USDT for USDT (50,000 credits).
        </p>
      </div>

      {/* Payment History */}
      {payments.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow overflow-x-auto">
          <h2 className="text-xl font-bold mb-4">Payment History</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credits</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid At</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-4 py-3 text-sm capitalize">{payment.payment_type.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-sm">
                      {payment.payment_method === 'usdt' ? `${payment.amount} USDT` : `₦${payment.amount.toLocaleString()}`}
                    </td>
                    <td className="px-4 py-3 text-sm">{payment.credits ?? '-'}</td>
                    <td className="px-4 py-3 text-sm font-mono">{payment.reference}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium
                        ${payment.payment_status === 'success' ? 'bg-green-100 text-green-800' : 
                          payment.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'}`}>
                        {payment.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm capitalize">{payment.payment_method}</td>
                    <td className="px-4 py-3 text-sm">{payment.paid_at ? new Date(payment.paid_at).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Manual Payment Requests */}
      {manualRequests.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow overflow-x-auto">
          <h2 className="text-xl font-bold mb-4">Manual Payment Requests</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested At</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approved At</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {manualRequests.map((req) => (
                  <tr key={req.id}>
                    <td className="px-4 py-3 text-sm">
                      {req.reference.startsWith('USDT') ? `${(req.amount/100).toFixed(2)} USDT` : `₦${req.amount.toLocaleString()}`}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">{req.reference}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium
                        ${req.status === 'approved' ? 'bg-green-100 text-green-800' : 
                          req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'}`}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{new Date(req.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm">{req.approved_at ? new Date(req.approved_at).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== USDT Payment Modal ===== */}
      {showUSDTModal && usdtDetails && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <h3 className="text-lg font-bold mb-3 text-center">USDT Payment Details</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Amount to Send</p>
                <p className="font-bold text-lg text-blue-600">{usdtDetails.amount_usdt.toFixed(6)} USDT <span className="text-xs font-normal text-gray-500">(ERC20)</span></p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Credits to Receive</p>
                <p className="font-medium">{usdtDetails.credits.toLocaleString()} credits</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Wallet Address</p>
                <div className="flex items-center gap-2 bg-gray-100 p-2 rounded-lg mt-1">
                  <code className="text-xs break-all font-mono flex-1">{usdtDetails.wallet_address}</code>
                  <button onClick={() => copyToClipboard(usdtDetails.wallet_address)} className="text-blue-600 hover:text-blue-800 flex-shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                  </button>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Reference</p>
                <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded block break-all">{usdtDetails.reference}</code>
              </div>
              <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-800 space-y-1">
                <p><strong>⚠️ Important:</strong> Send <strong>exactly {usdtDetails.amount_usdt.toFixed(6)} USDT</strong> on ERC20 network. After sending, master admin will verify.</p>
              </div>
              <div className="bg-blue-50 p-2 rounded text-xs text-blue-800">
                <p><strong>📞 Need help?</strong> Chat on WhatsApp: <a href="https://wa.me/2349039421996" target="_blank" rel="noopener" className="underline">+234 903 942 1996</a> or <a href="https://wa.me/2348113315252" target="_blank" rel="noopener" className="underline">+234 811 331 5252</a></p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => { setShowUSDTModal(false); setUsdtDetails(null); fetchFullData(); }} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Bank Transfer Modal ===== */}
      {showBankModal && bankDetails && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <h3 className="text-lg font-bold mb-3 text-center">Bank Transfer Details</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Amount to Pay</p>
                <p className="font-bold text-lg text-green-600">₦{bankDetails.amount.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bank Name</p>
                <p className="font-medium">{bankDetails.bank_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Account Number</p>
                <div className="flex items-center gap-2 bg-gray-100 p-2 rounded-lg mt-1">
                  <code className="text-sm font-mono flex-1">{bankDetails.account_number}</code>
                  <button onClick={() => copyToClipboard(bankDetails.account_number)} className="text-blue-600 hover:text-blue-800 flex-shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                  </button>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Account Name</p>
                <p className="font-medium">{bankDetails.account_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Reference</p>
                <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded block break-all">{bankDetails.reference}</code>
              </div>
              <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-800">
                <p><strong>⚠️ Important:</strong> Use the reference above when making the transfer. After payment, master admin will verify and add credits.</p>
              </div>
              <div className="bg-blue-50 p-2 rounded text-xs text-blue-800">
                <p><strong>📞 Need help?</strong> Chat on WhatsApp: <a href="https://wa.me/2349039421996" target="_blank" rel="noopener" className="underline">+234 903 942 1996</a> or <a href="https://wa.me/2348113315252" target="_blank" rel="noopener" className="underline">+234 811 331 5252</a></p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => { setShowBankModal(false); setBankDetails(null); fetchFullData(); }} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayFeePage;